import React, { useState } from 'react';
import { Box, Typography, Paper, TextField, Popover, Button, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { X } from 'lucide-react';
import { colors } from '../../../styles/theme/colors';
import CustomButton from '../Button/Custombutton';
import theme from '../../../styles/theme/theme';

// Utility functions for date handling
const getDaysInMonth = (year: number, month: number) => {
  return new Date(year, month + 1, 0).getDate();
};

const getFirstDayOfMonth = (year: number, month: number) => {
  return new Date(year, month, 1).getDay();
};

// Function to get an array of day numbers for a month
const getMonthDays = (year: number, month: number) => {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  // Convert Sunday-based index (0-6) to Monday-based (0-6 where 0 is Monday, 6 is Sunday)
  const firstDayMonday = firstDay === 0 ? 6 : firstDay - 1;

  // Get previous month days that appear in the calendar
  const prevMonthDays = [];
  if (firstDayMonday > 0) {
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const prevMonthDaysCount = getDaysInMonth(prevYear, prevMonth);

    for (let i = prevMonthDaysCount - firstDayMonday + 1; i <= prevMonthDaysCount; i++) {
      prevMonthDays.push({
        day: i,
        month: prevMonth,
        year: prevYear,
        isCurrentMonth: false,
      });
    }
  }

  // Get current month days
  const currentMonthDays = [];
  for (let i = 1; i <= daysInMonth; i++) {
    currentMonthDays.push({
      day: i,
      month,
      year,
      isCurrentMonth: true,
    });
  }

  // Get next month days to fill the calendar
  const totalDaysShown = Math.ceil((firstDayMonday + daysInMonth) / 7) * 7;
  const nextMonthDays = [];
  if (totalDaysShown > prevMonthDays.length + currentMonthDays.length) {
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    const daysToAdd = totalDaysShown - (prevMonthDays.length + currentMonthDays.length);

    for (let i = 1; i <= daysToAdd; i++) {
      nextMonthDays.push({
        day: i,
        month: nextMonth,
        year: nextYear,
        isCurrentMonth: false,
      });
    }
  }

  return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
};

// Get month name
const getMonthName = (month: number) => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return months[month];
};

// Helper function to format date as string
const formatDate = (date: Date) => {
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
};

// Helper function to compare dates (year, month, day)
const isSameDay = (date1: Date, date2: Date) => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

// Helper function to check if a date is between start and end
const isInRange = (date: Date, startDate: Date | null, endDate: Date | null) => {
  if (!startDate || !endDate) return false;
  return date > startDate && date < endDate;
};

// Helper function to check if a date is in the past
const isPastDate = (date: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

// Styled components
const CalendarContainer = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: '700px',
  borderRadius: '16px',
  boxShadow: 'none',
  overflow: 'visible',
  backgroundColor: 'transparent',
  border: 'none',
  padding: 0,
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
  },
}));

const CalendarHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 20px 16px 16px',
  [theme.breakpoints.down('sm')]: {
    padding: '12px',
  },
}));

const CalendarGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '8px',
  padding: '0 20px 16px 16px',
  '& > div': {
    textAlign: 'center',
  },
  [theme.breakpoints.down('sm')]: {
    gap: '4px',
    padding: '0 12px 12px 12px',
  },
}));

const DayLabel = styled(Typography)({
  fontFamily: theme.typography.fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  color: colors.neutral.gray700,
  padding: '8px 0',
});

const DayCell = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'isCurrentMonth' && prop !== 'isSelected' && prop !== 'isInRange' && prop !== 'isToday' && prop !== 'isDisabled'
})<{
  isCurrentMonth: boolean;
  isSelected: boolean;
  isInRange: boolean;
  isToday: boolean;
  isDisabled: boolean;
}>(({ isCurrentMonth, isSelected, isInRange, isToday, isDisabled, theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '4px',
  width: '40px',
  height: '40px',
  margin: '0 auto',
  borderRadius: '50%',
  cursor: isDisabled ? 'not-allowed' : 'pointer',
  position: 'relative',
  backgroundColor: isSelected ? colors.traveller.primary : 'transparent',
  color: isSelected
    ? colors.neutral.white
    : isDisabled
      ? colors.neutral.gray300
      : !isCurrentMonth
        ? colors.neutral.gray400
        : colors.neutral.gray900,
  opacity: isDisabled ? 0.4 : !isCurrentMonth ? 0.5 : 1,
  fontFamily: theme.typography.fontFamily,
  fontSize: '14px',
  fontWeight: isSelected || isToday ? 500 : 400,
  pointerEvents: isDisabled ? 'none' : 'auto',
  '&:hover': {
    backgroundColor: isDisabled ? 'transparent' : isSelected ? colors.traveller.primary : 'rgba(122, 0, 86, 0.05)',
  },
  ...(isInRange && !isDisabled && {
    backgroundColor: 'rgba(122, 0, 86, 0.1)',
  }),
  ...(isToday && !isSelected && !isDisabled && {
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '4px',
      width: '4px',
      height: '4px',
      borderRadius: '50%',
      backgroundColor: colors.traveller.primary,
    },
  }),
  [theme.breakpoints.down('sm')]: {
    width: '32px',
    height: '32px',
    fontSize: '12px',
  },
}));

const CalendarFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  padding: '16px 20px 16px 16px',
  borderTop: `1px solid ${colors.neutral.gray200}`,
  gap: '16px',
  [theme.breakpoints.down('sm')]: {
    padding: '12px',
  },
}));

const DateDisplay = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '8px 12px',
  borderRadius: '8px',
  border: `1px solid ${colors.neutral.gray300}`,
  fontSize: '16px',
  color: colors.neutral.gray900,
  fontFamily: theme.typography.fontFamily,
});

// Main DateRangePicker component
interface DateRangePickerProps {
  value: [Date | null, Date | null];
  onChange: (value: [Date | null, Date | null]) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  userType?: 'traveller' | 'sender' | 'default';
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  sx?: unknown;
  hideIcon?: boolean;
  allowPastDates?: boolean;
  showClearButton?: boolean;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value: [startDate, endDate],
  onChange,
  label = 'Date Range',
  placeholder = 'Select date range',
  disabled = false,
  error = false,
  helperText,
  fullWidth = true,
  userType = 'traveller',
  open: controlledOpen,
  onOpen,
  onClose: onCloseProp,
  sx,
  hideIcon = false,
  allowPastDates = false,
  showClearButton = false,
}) => {
  // State for the calendar
  const today = new Date();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [leftMonth, setLeftMonth] = useState(today.getMonth());
  const [leftYear, setLeftYear] = useState(today.getFullYear());
  const [rightMonth, setRightMonth] = useState(
    today.getMonth() === 11 ? 0 : today.getMonth() + 1
  );
  const [rightYear, setRightYear] = useState(
    today.getMonth() === 11 ? today.getFullYear() + 1 : today.getFullYear()
  );

  // State for selection process
  const [selecting, setSelecting] = useState<'start' | 'end' | null>(null);
  const [tempStart, setTempStart] = useState<Date | null>(startDate);
  const [tempEnd, setTempEnd] = useState<Date | null>(endDate);

  // Determine primary color based on userType
  const primaryColor = userType === 'traveller'
    ? colors.traveller.primary
    : userType === 'sender'
      ? colors.sender.primary
      : colors.neutral.gray700;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
      setTempStart(startDate);
      setTempEnd(endDate);
      setSelecting('start'); // Initialize selection state

      // If dates are already selected, adjust the calendar view
      if (startDate) {
        setLeftMonth(startDate.getMonth());
        setLeftYear(startDate.getFullYear());

        const nextMonth = startDate.getMonth() === 11 ? 0 : startDate.getMonth() + 1;
        const nextYear = startDate.getMonth() === 11 ? startDate.getFullYear() + 1 : startDate.getFullYear();

        setRightMonth(nextMonth);
        setRightYear(nextYear);
      }

      // Call parent's onOpen if provided
      if (onOpen) {
        onOpen();
      }
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelecting(null);
    // Reset temp values on close without applying
    setTempStart(startDate);
    setTempEnd(endDate);

    // Call parent's onClose if provided
    if (onCloseProp) {
      onCloseProp();
    }
  };

  const handlePreviousMonth = () => {
    // Move left calendar back by 1 month
    setLeftMonth(prev => prev === 0 ? 11 : prev - 1);
    setLeftYear(prev => leftMonth === 0 ? prev - 1 : prev);

    // Move right calendar back by 1 month
    setRightMonth(prev => prev === 0 ? 11 : prev - 1);
    setRightYear(prev => rightMonth === 0 ? prev - 1 : prev);
  };

  const handleNextMonth = () => {
    // Move left calendar forward by 1 month
    setLeftMonth(prev => prev === 11 ? 0 : prev + 1);
    setLeftYear(prev => leftMonth === 11 ? prev + 1 : prev);

    // Move right calendar forward by 1 month
    setRightMonth(prev => prev === 11 ? 0 : prev + 1);
    setRightYear(prev => rightMonth === 11 ? prev + 1 : prev);
  };

  const handleDayClick = (year: number, month: number, day: number) => {
    const clickedDate = new Date(year, month, day);

    if (!selecting || selecting === 'start') {
      setTempStart(clickedDate);
      setTempEnd(null);
      setSelecting('end');
    } else if (selecting === 'end') {
      if (tempStart && clickedDate < tempStart) {
        // If end date is before start date, swap them
        setTempEnd(tempStart);
        setTempStart(clickedDate);
      } else {
        setTempEnd(clickedDate);
      }
      setSelecting(null);
    }
  };

  const handleApply = () => {
    onChange([tempStart, tempEnd]);
    handleClose();
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([null, null]);
    setTempStart(null);
    setTempEnd(null);
  };

  // Format the display value
  const displayValue = () => {
    if (startDate && endDate) {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    } else if (startDate) {
      return formatDate(startDate);
    }
    return '';
  };

  const leftMonthDays = getMonthDays(leftYear, leftMonth);
  const rightMonthDays = getMonthDays(rightYear, rightMonth);

  // Use internal state for open (anchorEl), controlled open from parent is only for external control
  const isOpen = Boolean(anchorEl);

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
     <TextField
        label={label}
        value={displayValue()}
        placeholder={placeholder}
        onClick={handleOpen}
        fullWidth={fullWidth}
        disabled={disabled}
        error={error}
        helperText={helperText}
        slotProps={{
          input: {
            readOnly: true,
            startAdornment: !hideIcon ? (
              <CalendarTodayIcon
                sx={{
                  marginRight: '8px',
                  color: primaryColor
                }}
              />
            ) : undefined,
            endAdornment: showClearButton && (startDate || endDate) ? (
              <IconButton
                onClick={handleClear}
                size="small"
                sx={{
                  padding: '2px',
                  position: 'absolute',
                  right: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <X size={16} color="#6A6A6A" />
              </IconButton>
            ) : undefined,
            sx: {
              paddingRight: showClearButton && (startDate || endDate) ? '24px' : undefined,
            },
          },
          inputLabel: {
            shrink: true,
          },
        }}
        sx={{
          cursor: 'pointer',
          '& .MuiOutlinedInput-root': {
            fontFamily: theme.typography.fontFamily,
            fontSize: '14px',
            borderRadius: '8px',
            backgroundColor: colors.neutral.white,
            '& fieldset': {
              borderColor: error ? colors.semantic.error : colors.form.border,
            },
            '&:hover fieldset': {
              borderColor: error ? colors.semantic.error : primaryColor,
            },
            '&.Mui-focused fieldset': {
              borderColor: error ? colors.semantic.error : primaryColor,
              borderWidth: '1px',
            },
          },
          '& .MuiInputBase-root': {
            fontSize: '14px',
            fontWeight: 400,
            fontFamily: theme.typography.fontFamily,
          },
          '& .MuiInputBase-input': {
            '&::placeholder': {
              fontFamily: theme.typography.fontFamily,
              opacity: 1,
              color: colors.neutral.gray400,
            },
          },
          '& .MuiFormHelperText-root': {
            fontSize: { xs: "0.6rem", sm: "0.7rem" },
            position: "absolute",
            bottom: { xs: -16, sm: -20 },
            margin: 0,
          },
          '& .MuiInputLabel-root': {
            fontFamily: theme.typography.fontFamily,
            fontSize: '14px',
            color: error ? colors.semantic.error : colors.form.label,
            '&.Mui-focused': {
              color: error ? colors.semantic.error : primaryColor,
            },
          },
          '& .MuiInputLabel-shrink': {
            transform: 'none',
          },
          ...sx,
        }}
      />

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: '16px',
            boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.12)',
            overflowY: 'auto',
            overflowX: 'hidden',
            width: { xs: 'calc(100vw - 32px)', sm: '731px' },
            maxWidth: '100%',
            maxHeight: { xs: '85vh', sm: '90vh' },
            border: '1px solid rgba(0, 0, 0, 0.12)',
            padding: { xs: '8px 12px 12px', sm: '8px 20px 12px 12px' },
            backgroundColor: colors.neutral.white,
          },
        }}
      >
        <CalendarContainer elevation={0}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: '8px', md: 0 } }}>
            {/* Left Month */}
            <Box sx={{ flex: 1, borderRight: { xs: 'none', md: `1px solid ${colors.neutral.gray200}` } }}>
              <CalendarHeader>
                <IconButton onClick={handlePreviousMonth} size="small">
                  <ChevronLeftIcon sx={{ color: primaryColor }} />
                </IconButton>
                <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: '16px', fontWeight: 500 }}>
                  {`${getMonthName(leftMonth)} ${leftYear}`}
                </Typography>
                {/* Empty space to align with right calendar */}
                <Box sx={{ width: '40px' }} />
              </CalendarHeader>

              <CalendarGrid>
                {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(day => (
                  <DayLabel key={day}>{day}</DayLabel>
                ))}

                {leftMonthDays.map(({ day, month, year, isCurrentMonth }, index) => {
                  const date = new Date(year, month, day);
                  const isToday = isSameDay(date, today);
                  const isSelectedStart = !!(tempStart && isSameDay(date, tempStart));
                  const isSelectedEnd = !!(tempEnd && isSameDay(date, tempEnd));
                  const isInDateRange = !!(tempStart && tempEnd && isInRange(date, tempStart, tempEnd));
                  const isDisabled = allowPastDates ? false : isPastDate(date);

                  return (
                    <DayCell
                      key={`left-${index}`}
                      isCurrentMonth={isCurrentMonth}
                      isSelected={isSelectedStart || isSelectedEnd}
                      isInRange={isInDateRange}
                      isToday={isToday}
                      isDisabled={isDisabled}
                      onClick={() => !isDisabled && handleDayClick(year, month, day)}
                    >
                      {day}
                    </DayCell>
                  );
                })}
              </CalendarGrid>
            </Box>

            {/* Right Month */}
            <Box sx={{ flex: 1 }}>
              <CalendarHeader>
                {/* Empty space to align with left calendar */}
                <Box sx={{ width: '40px' }} />
                <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: '16px', fontWeight: 500 }}>
                  {`${getMonthName(rightMonth)} ${rightYear}`}
                </Typography>
                <IconButton onClick={handleNextMonth} size="small">
                  <ChevronRightIcon sx={{ color: primaryColor }} />
                </IconButton>
              </CalendarHeader>

              <CalendarGrid>
                {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(day => (
                  <DayLabel key={day}>{day}</DayLabel>
                ))}

                {rightMonthDays.map(({ day, month, year, isCurrentMonth }, index) => {
                  const date = new Date(year, month, day);
                  const isToday = isSameDay(date, today);
                  const isSelectedStart = !!(tempStart && isSameDay(date, tempStart));
                  const isSelectedEnd = !!(tempEnd && isSameDay(date, tempEnd));
                  const isInDateRange = !!(tempStart && tempEnd && isInRange(date, tempStart, tempEnd));
                  const isDisabled = allowPastDates ? false : isPastDate(date);

                  return (
                    <DayCell
                      key={`right-${index}`}
                      isCurrentMonth={isCurrentMonth}
                      isSelected={isSelectedStart || isSelectedEnd}
                      isInRange={isInDateRange}
                      isToday={isToday}
                      isDisabled={isDisabled}
                      onClick={() => !isDisabled && handleDayClick(year, month, day)}
                    >
                      {day}
                    </DayCell>
                  );
                })}
              </CalendarGrid>
            </Box>
          </Box>

          <CalendarFooter>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: '16px', width: '100%', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' } }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: '12px', alignItems: 'center' }}>
                {tempStart && (
                  <DateDisplay>
                    {formatDate(tempStart)} {tempEnd && `– ${formatDate(tempEnd)}`}
                  </DateDisplay>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: '12px', width: { xs: '100%', sm: 'auto' } }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{
                    borderRadius: '8px',
                    padding: '10px 20px',
                    textTransform: 'none',
                    borderColor: colors.neutral.gray300,
                    color: colors.neutral.gray900,
                    fontFamily: theme.typography.fontFamily,
                    flex: { xs: 1, sm: 'none' },
                    '&:hover': {
                      borderColor: colors.neutral.gray400,
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    }
                  }}
                >
                  Cancel
                </Button>

                <CustomButton
                  color="primary"
                  size="lg"
                  onClick={handleApply}
                  disabled={!tempStart || !tempEnd}
                  buttonText="Apply"
                  sx={{
                    backgroundColor: primaryColor,
                    flex: { xs: 1, sm: 'none' },
                    '&:hover': {
                      backgroundColor: primaryColor === colors.traveller.primary
                        ? '#650047'
                        : primaryColor === colors.sender.primary
                          ? '#015A80'
                          : '#505050', // Darker shade of default gray
                    },
                  }}
                />
              </Box>
            </Box>
          </CalendarFooter>
        </CalendarContainer>
      </Popover>
    </Box>
  );
};

export default DateRangePicker;